import { onError } from '@apollo/client/link/error';
import { ApolloError } from '@apollo/client';

export const createErrorLink = (
	onUnauthorized: () => void,
	/**
	 * A mutation refused because the session is an admin's read-only guest
	 * preview. The backend tags these with `extensions.guestPreview` and names
	 * the operation in the message.
	 *
	 * Handled centrally on purpose: the refusal list is deny-by-default and
	 * lives in the backend, so a mutation added tomorrow is blocked before
	 * anyone remembers to disable its button. Disabling the obvious controls is
	 * the courtesy; this is the net under it.
	 */
	onGuestPreviewBlocked?: (message: string) => void
) =>
	onError(({ graphQLErrors }) => {
		if (graphQLErrors) {
			for (const err of graphQLErrors) {
				// Handle authentication errors
				if (
					err.extensions?.code === 'UNAUTHENTICATED' ||
					err?.message === 'Unauthenticated'
				) {
					onUnauthorized?.();
					break;
				}
				if (err.extensions?.guestPreview === true) {
					onGuestPreviewBlocked?.(err.message);
					break;
				}
			}
		}
	});

export const extractErrorMessage = (error: ApolloError): string => {
	if (error.graphQLErrors && error.graphQLErrors.length > 0) {
		const graphQLError = error.graphQLErrors[0];

		// Extract from originalError if it's a generic "Unexpected error."
		if (
			graphQLError.message === 'Unexpected error.' &&
			graphQLError.extensions?.originalError &&
			typeof graphQLError.extensions.originalError === 'object' &&
			'message' in graphQLError.extensions.originalError &&
			typeof graphQLError.extensions.originalError.message === 'string'
		) {
			return graphQLError.extensions.originalError.message;
		}

		return graphQLError.message;
	}

	return error.message;
};
